import { useMemo } from 'react';
import { motion } from 'framer-motion';
import { Sparkles } from 'lucide-react';
import type { Keyword, KeywordCluster } from '@seo/types';
import { fadeUp, stagger } from '@/components/motion';
import { cn } from '@/lib/cn';

export interface ClusterBubblesProps {
  clusters: KeywordCluster[];
  /** Optional keyword list to surface the top-3 terms per cluster. */
  keywords?: Keyword[];
  selectedId?: string;
  onSelect?: (id: string | undefined) => void;
  className?: string;
}

const PALETTE = [
  { ring: 'ring-sky-500/40', glow: 'from-sky-500/30 to-sky-500/0' },
  { ring: 'ring-violet-500/40', glow: 'from-violet-500/30 to-violet-500/0' },
  { ring: 'ring-emerald-500/40', glow: 'from-emerald-500/30 to-emerald-500/0' },
  { ring: 'ring-amber-500/40', glow: 'from-amber-500/30 to-amber-500/0' },
  { ring: 'ring-rose-500/40', glow: 'from-rose-500/30 to-rose-500/0' },
  { ring: 'ring-cyan-500/40', glow: 'from-cyan-500/30 to-cyan-500/0' },
];

function fmtVolume(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}

export function ClusterBubbles({
  clusters,
  keywords,
  selectedId,
  onSelect,
  className,
}: ClusterBubblesProps): JSX.Element {
  const topTermsByCluster = useMemo(() => {
    const map = new Map<string, Keyword[]>();
    if (!keywords) return map;
    for (const k of keywords) {
      if (!k.cluster_id) continue;
      const arr = map.get(k.cluster_id) ?? [];
      arr.push(k);
      map.set(k.cluster_id, arr);
    }
    for (const [id, arr] of map) {
      arr.sort((a, b) => (b.opportunity ?? 0) - (a.opportunity ?? 0));
      map.set(id, arr.slice(0, 3));
    }
    return map;
  }, [keywords]);

  const maxSize = useMemo(
    () => clusters.reduce((acc, c) => Math.max(acc, c.size), 1),
    [clusters],
  );

  return (
    <motion.ul
      variants={stagger}
      initial="hidden"
      animate="show"
      className={cn(
        'grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4',
        className,
      )}
    >
      {clusters.map((c, i) => {
        const palette = PALETTE[i % PALETTE.length]!;
        const sizeRatio = Math.max(0.45, c.size / maxSize);
        const isActive = selectedId === c.id;
        const topTerms = topTermsByCluster.get(c.id) ?? [];
        return (
          <motion.li key={c.id} variants={fadeUp}>
            <button
              type="button"
              onClick={() =>
                onSelect?.(selectedId === c.id ? undefined : c.id)
              }
              className={cn(
                'group relative flex h-full w-full flex-col overflow-hidden rounded-2xl border border-border/60 bg-card/60 p-4 text-left backdrop-blur transition-all hover:border-border hover:shadow-glow-sm',
                isActive && 'ring-2 ring-primary/60 shadow-glow-sm',
              )}
            >
              <div
                aria-hidden
                className={cn(
                  'pointer-events-none absolute -right-12 -top-12 h-40 w-40 rounded-full bg-gradient-radial blur-2xl bg-gradient-to-br opacity-60 transition-opacity group-hover:opacity-90',
                  palette.glow,
                )}
                style={{ transform: `scale(${sizeRatio})` }}
              />
              <div className="relative flex items-start justify-between gap-2">
                <div
                  className={cn(
                    'flex h-9 w-9 items-center justify-center rounded-full bg-background/70 ring-1',
                    palette.ring,
                  )}
                >
                  <Sparkles className="h-4 w-4 text-foreground/80" />
                </div>
                <div className="text-right">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                    Volume
                  </div>
                  <div className="font-mono text-sm text-foreground">
                    {fmtVolume(c.total_volume)}
                  </div>
                </div>
              </div>
              <div className="relative mt-3">
                <div className="line-clamp-2 text-base font-semibold text-foreground">
                  {c.label}
                </div>
                {c.description ? (
                  <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">
                    {c.description}
                  </p>
                ) : null}
              </div>
              <div className="relative mt-3 flex items-center gap-3 text-[11px] text-muted-foreground">
                <span>
                  <span className="font-mono text-foreground">{c.size}</span> keywords
                </span>
                {c.avg_difficulty !== null ? (
                  <span>
                    avg diff{' '}
                    <span className="font-mono text-foreground">{c.avg_difficulty}</span>
                  </span>
                ) : null}
              </div>
              {topTerms.length > 0 ? (
                <ul className="relative mt-3 space-y-1 border-t border-border/60 pt-2 text-xs">
                  {topTerms.map((t) => (
                    <li
                      key={t.id}
                      className="flex items-center justify-between gap-2 truncate"
                    >
                      <span className="truncate text-foreground/90">{t.term}</span>
                      <span className="font-mono text-muted-foreground">
                        {t.opportunity ?? '—'}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : null}
            </button>
          </motion.li>
        );
      })}
    </motion.ul>
  );
}
